import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import { Shield, Search, Filter } from './components/DeskeyIcons';
import { Card, CardContent } from './components/ui/card';
import { Input } from './components/ui/input';
import { Skeleton } from './components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getActionColor = (action) => {
  if (action?.includes('login')) return 'bg-blue-50 text-blue-700';
  if (action?.includes('publish')) return 'bg-green-50 text-green-700';
  if (action?.includes('delete') || action?.includes('deactivate')) return 'bg-red-50 text-red-700';
  if (action?.includes('update') || action?.includes('updated')) return 'bg-amber-50 text-amber-700';
  return 'bg-gray-100 text-gray-600';
};

export default function AdminAuditLogs() {
  const { user, token } = useApp();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/audit-logs`, { params: { tenant_id: user?.tenant_id } , headers })
      .then(r => setLogs(r.data))
      .catch(() => toast.error('Failed to load audit logs'))
      .finally(() => setLoading(false));
  }, [user?.tenant_id]);

  const filtered = logs.filter(l =>
    l.action?.toLowerCase().includes(search.toLowerCase()) ||
    l.user_id?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardShell title="Audit Logs" breadcrumb="Admin Dashboard">
      <div className="space-y-4 fade-in">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'hsl(var(--muted-foreground))' }} />
            <Input placeholder="Search logs..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
          </div>
          <span className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>{filtered.length} entries</span>
        </div>

        {loading ? (
          <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Shield className="empty-state-icon" />
            <p className="text-sm">No audit logs found</p>
          </div>
        ) : (
          <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full desky-table">
                  <thead>
                    <tr>
                      <th className="text-left">Action</th>
                      <th className="text-left hidden md:table-cell">Resource</th>
                      <th className="text-left hidden sm:table-cell">User ID</th>
                      <th className="text-left">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(l => (
                      <tr key={l.id}>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getActionColor(l.action)}`}>
                            {l.action}
                          </span>
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell">
                          <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                            {l.resource_type || '—'} {l.resource_id ? `(${l.resource_id.slice(0, 8)}...)` : ''}
                          </span>
                        </td>
                        <td className="py-3 px-4 hidden sm:table-cell">
                          <span className="text-xs font-mono">{l.user_id?.slice(0, 12)}...</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                            {new Date(l.created_at).toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardShell>
  );
}
